import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Message = {
  id: string;
  sender_type: "patient" | "ai" | "staff" | "system";
  sender_name: string | null;
  body: string;
  created_at: string;
};

type Thread = {
  id: string;
  state: "ai_active" | "escalated" | "doctor_active" | "resolved";
  assigned_staff_name: string | null;
};

type Props = {
  threadId: string;
  patientId: string;
  patientName: string;
  onClose: () => void;
  onChanged: () => void;
};

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The conversation, for a doctor.
 *
 * The server refuses this to a nurse or a clinic administrator with a 403,
 * which this panel shows as written rather than as an error: they saw the row
 * in the queue and routing a thread is not reading it (MSG-F-021). Telling them
 * plainly is better than an error that reads like a fault.
 *
 * Opening the panel does not accept the thread. Reading and taking are separate
 * acts, because a doctor scanning what came in should not silently become the
 * person answering it. Replying accepts it, which is the one shortcut worth
 * having: a doctor who reads and answers in one motion should not have to press
 * a button in between.
 */
export function ThreadPanel({
  threadId,
  patientId,
  patientName,
  onClose,
  onChanged,
}: Props) {
  const [thread, setThread] = useState<Thread | null>(null);
  const [items, setItems] = useState<Message[]>([]);
  const [refused, setRefused] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const foot = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const { data, error: failed } = await api.GET("/portal/threads/{id}", {
      params: { header: clinicHeader(), path: { id: threadId } },
    });
    if (failed) {
      const problem = readError(failed);
      // Not a fault. A nurse or administrator sees the queue and not this.
      if (problem.code === "role_not_permitted") setRefused(problem.message);
      else setError(problem.message);
      return;
    }
    setRefused(null);
    setError(null);
    setThread(data?.thread as Thread);
    setItems((data?.items ?? []) as Message[]);
  }, [threadId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    foot.current?.scrollIntoView({ block: "end" });
  }, [items.length]);

  async function send() {
    const body = draft.trim();
    if (!body) return;

    setBusy(true);
    const { error: failed } = await api.POST("/portal/threads/{id}/messages", {
      params: { header: clinicHeader(), path: { id: threadId } },
      body: { body },
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setDraft("");
    await load();
    onChanged();
  }

  async function close() {
    setBusy(true);
    const { error: failed } = await api.POST("/portal/threads/{id}/resolve", {
      params: { header: clinicHeader(), path: { id: threadId } },
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onChanged();
    onClose();
  }

  return (
    <div className="panel thread">
      <div className="row-between">
        <h2>
          <Link to={`/patients/${patientId}`}>{patientName}</Link>
        </h2>
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </div>

      <ErrorNote message={error} />

      {refused && (
        <p className="note">
          {refused} You can see this thread in the queue and route it to a doctor,
          and the conversation itself is theirs to read.
        </p>
      )}

      {!refused && thread && (
        <>
          <p className="quiet">
            {thread.state === "doctor_active" && thread.assigned_staff_name
              ? `${thread.assigned_staff_name} has this.`
              : "Nobody has taken this yet. Replying takes it."}
          </p>

          <div className="transcript">
            {items.map((m) => (
              <div key={m.id} className={`bubble from-${m.sender_type}`}>
                <div className="who">
                  {m.sender_type === "patient" && patientName}
                  {m.sender_type === "staff" && (m.sender_name ?? "The clinic")}
                  {m.sender_type === "ai" && "Assistant"}
                  {m.sender_type === "system" && "Shown to the patient"}
                  <span className="when">{clock(m.created_at)}</span>
                </div>
                <p>{m.body}</p>
              </div>
            ))}
            <div ref={foot} />
          </div>

          <label className="note-wrap" htmlFor="reply">
            <span>Your reply</span>
            <textarea
              id="reply"
              rows={3}
              value={draft}
              maxLength={4000}
              disabled={thread.state === "resolved"}
              placeholder="The patient sees this exactly as written."
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>

          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={busy || !draft.trim() || thread.state === "resolved"}
              onClick={() => void send()}
            >
              Send
            </button>
            {thread.state !== "resolved" && (
              <button type="button" className="link" disabled={busy} onClick={() => void close()}>
                Mark resolved
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
